import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { ComboSelect } from '../../../components/ComboSelect';
import { FormTitle } from '../../../components/FormTitle';
import { GridSearch } from '../../../components/GridSearch';
import { useConfirm } from '../../../components/confirm';
import { serverMessage } from '../../../components/confirm/serverMessage';
import { BusyIcon, runLoading } from '../../../components/loading';
import { useNotify } from '../../../components/notifications';
import { fmtLocalDate } from '../../../utils/datetime';
import { useNavActions } from '../../../hooks/useNavActions';
import { SettingsDetailPage, type DetailConfig } from '../../modules/SettingsDetailPage';
import { fetchPlmDailyQcList, fetchPlmQcHistory } from './plm.api';
import { dailyQcPoints, toPoints, type QcPoint } from './plmQcStats';
import { PlmReferenceTypesEditor } from './PlmReferenceTypesEditor';
import {
  EMPTY_LOOKUPS, FEW_COUNTS, LIMIT_BASES, LIMIT_SCOPES, LIMIT_WINDOW_MONTHS, PLM_REF_LIB_BASE,
  PLM_REF_LIB_FORM_NAME, PLM_REF_LIB_PAGE_ID, RANGE_RULE_HINT, STATUS_FILTERS, YES_NO,
  birefringenceLabel, calculateLimits, canLoadLimits, categoryLabel, effectiveRange, fmt2,
  hasAnyLimit, limitSeries, matchesSearch, matchesStatus, meanRiText, rangeText, recentMonths,
  riLimitsText, ruleRange, scopeLabel, scopeNeedsAnalyst, statusCounts, toActiveLimit,
  toLimitUploadRows, toSlideRow,
  type CalculatedLimit, type LimitBasis, type LimitScope, type PlmActiveLimit,
  type PlmReferenceLookups, type PlmReferenceSlideRow, type PlmReferenceTypeRow, type StatusFilter,
} from './plmReferenceLibrary';
import './plm.css';

type TestKind = 'PLM' | 'PCM';

const RETIRE_MESSAGE =
  'It is retired, not deleted — past QC reads keep pointing at it — and can no longer be picked '
  + 'for a new reference read. Reason (required — audited):';

/**
 * Settings ▸ Analysis ▸ Reference Library (prototype Reference-Library-v7,
 * review 10/07; first built 2026-09-28 from BTLIMS/EAS ucReferenceLibrary.vb).
 *
 * **List → full-page detail.** The list shows a slide's GENERAL information
 * only; its asbestos types — one row per type, each with its own %, acceptable
 * range, optics and RI limits — open under the row ("+", or the Types count).
 * New, the pencil, Edit and a double-click open the ONE shared
 * `<SettingsDetailPage>` (standing rule 12): General · Asbestos types ·
 * Certificate, saved as one unit. The asbestos types are the page's buffered
 * child (`PlmReferenceTypesEditor`).
 *
 * **Delete retires.** A slide is never deleted from here: past QC reads point at
 * it. Retiring needs a reason (audited) and takes the slide out of the Daily QC
 * picker; a retired slide can be re-activated from its detail.
 *
 * **Control limits are calculated, then uploaded.** The panel under the list
 * works them out from the SAME points the PLM QC charts plot, per concentration
 * category, on an SD or an IQR basis; "Upload as active" stores the reviewed set
 * as the active limits of the chosen scope and analyst.
 *
 * **PCM is a placeholder.** The PLM | PCM switch is here because the library
 * will hold PCM slides too, but their fields are still to be agreed with the
 * lab, so the PCM side lists nothing and adds nothing yet.
 */
export function PlmReferenceLibraryPage({ onHome }: { onHome: () => void }) {
  const notify = useNotify();
  const { prompt } = useConfirm();
  // Module Access Control: New needs Create, Edit / Add type / Upload need Write,
  // Delete (= retire) needs Delete. A Read-only role does not SEE those actions
  // (hidden, never greyed); the API refuses the writes with 403 too.
  const perm = useNavActions(PLM_REF_LIB_PAGE_ID);

  const [slides, setSlides] = useState<PlmReferenceSlideRow[]>([]);
  const [lookups, setLookups] = useState<PlmReferenceLookups>(EMPTY_LOOKUPS);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);

  const [test, setTest] = useState<TestKind>('PLM');
  const [status, setStatus] = useState<StatusFilter>('Active');
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState<Set<string | number>>(new Set());
  /** The rows whose asbestos types are showing. */
  const [open, setOpen] = useState<Set<string | number>>(new Set());

  // The open detail view (list → detail replaces the whole page, rule 12).
  const [detail, setDetail] = useState<{ id: string | null; addType?: boolean } | null>(null);

  // ── loads ────────────────────────────────────────────────────────────────

  const loadSlides = useCallback(async () => {
    const started = performance.now();
    setLoading(true);
    try {
      const res = await api.get<{ data?: Record<string, unknown>[] }>(`${PLM_REF_LIB_BASE}/samples`);
      setSlides((res.data?.data ?? []).map(toSlideRow));
      setSel(new Set());
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - started);
    }
  }, []);

  const loadLookups = useCallback(async () => {
    const res = await api.get<{ data?: PlmReferenceLookups }>(`${PLM_REF_LIB_BASE}/lookups`);
    setLookups({ ...EMPTY_LOOKUPS, ...(res.data?.data ?? {}) });
  }, []);

  useEffect(() => { void loadLookups(); }, [loadLookups]);
  useEffect(() => { void loadSlides(); }, [loadSlides]);

  // ── the list ─────────────────────────────────────────────────────────────

  const plm = test === 'PLM';
  // PCM holds nothing yet (see the header), so its list is simply empty.
  const all = plm ? slides : [];
  const counts = useMemo(() => statusCounts(all), [all]);
  const visible = useMemo(
    () => all.filter((r) => matchesStatus(r, status) && matchesSearch(r, search)),
    [all, status, search]);

  const openDetail = (id: string | null, addType = false) => setDetail({ id, addType });
  const toggleOpen = (id: string) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const selected = slides.filter((r) => sel.has(r.id));

  /** Toolbar Delete — a slide is RETIRED, with a reason, never deleted. */
  const retireSelected = async () => {
    const live = selected.filter((r) => r.status !== 'Retired');
    if (live.length === 0) {
      notify.warning(selected.length ? 'The selected slide is already retired.' : 'Select a reference slide to retire.');
      return;
    }
    const what = live.length === 1 ? live[0]!.referenceId : `${live.length} reference slides`;
    const reason = await prompt({
      title: 'Retire reference slide',
      message: `Retire ${what}? ${RETIRE_MESSAGE}`,
      placeholder: 'Reason',
      required: true,
      confirmLabel: 'Retire',
    });
    if (!reason?.trim()) return;
    try {
      await runLoading((async () => {
        for (const r of live) await api.post(`${PLM_REF_LIB_BASE}/samples/${r.id}/retire`, { reason: reason.trim() });
      })(), { blocking: true, label: 'Retiring…' });
      notify.success('Retire completed successfully.');
    } catch (e) {
      notify.error(serverMessage(e) ?? 'Retire failed.');
    } finally {
      await loadSlides();
    }
  };

  // ── columns ──────────────────────────────────────────────────────────────

  const columns: AppColumn<PlmReferenceSlideRow>[] = useMemo(() => [
    {
      field: 'id', header: '', width: 40, align: 'center', sortable: false, filterable: false, noExport: true,
      // The pencil opens the record for editing — Write only (hidden otherwise).
      render: (r) => (perm.write
        ? (
          <button type="button" className="plm-reflib-open" title="Open" aria-label={`Open ${r.referenceId}`}
            onClick={(e) => { e.stopPropagation(); openDetail(r.id); }}>
            <i className="ti ti-pencil" aria-hidden="true" />
          </button>
        )
        : null),
    },
    { field: 'referenceId', header: 'ReferenceID', width: 125 },
    { field: 'source', header: 'Source', width: 160 },
    { field: 'originalSourceId', header: 'OriginalSourceID', width: 175 },
    { field: 'matrix', header: 'Matrix', width: 120 },
    { field: 'material', header: 'Material', width: 190 },
    {
      field: 'typeCount', header: 'Types', width: 85, align: 'right',
      value: (r) => r.typeCount,
      // Export and the column filter read what the count stands for.
      text: (r) => r.typesText,
      render: (r) => (r.typeCount
        ? (
          <button type="button" className="plm-reflib-tcount" title="Show asbestos types"
            aria-label={`${r.typeCount} asbestos type${r.typeCount === 1 ? '' : 's'} — show`}
            onClick={(e) => { e.stopPropagation(); toggleOpen(r.id); }}>
            {r.typeCount}
          </button>
        )
        : <span className="plm-reflib-muted">ND</span>),
    },
    { field: 'storage', header: 'Storage', width: 150 },
    {
      field: 'attachmentName', header: 'Certificate', width: 125, sortable: false,
      text: (r) => (r.attachmentOid ? r.attachmentName || 'On file' : ''),
      render: (r) => (r.attachmentOid
        ? (
          <a className="plm-reflib-cert" href={`${PLM_REF_LIB_BASE}/file/${r.attachmentOid}/download`}
            target="_blank" rel="noreferrer" title={r.attachmentName} onClick={(e) => e.stopPropagation()}>
            <i className="ti ti-paperclip" aria-hidden="true" /> on file
          </a>
        )
        : null),
    },
    { field: 'status', header: 'Status', type: 'status', width: 115 },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [perm.write]);

  /** The PCM side: only the HEADINGS its list will have (draft — see the header).
   *  It never holds a row, so the two PCM-only headings borrow fields that exist
   *  on the row type purely to have a key; nothing is ever read through them. */
  const pcmColumns: AppColumn<PlmReferenceSlideRow>[] = useMemo(() => [
    { field: 'referenceId', header: 'ReferenceID', width: 125 },
    { field: 'source', header: 'Source', width: 160 },
    { field: 'originalSourceId', header: 'OriginalSourceID', width: 175 },
    { field: 'matrix', header: 'Matrix', width: 120 },
    { field: 'material', header: 'Fibre density (f/mm²)', width: 170, align: 'right' },
    { field: 'typesText', header: 'Acceptable range', width: 150, align: 'right' },
    { field: 'storage', header: 'Storage', width: 150 },
    { field: 'attachmentName', header: 'Certificate', width: 125 },
    { field: 'status', header: 'Status', type: 'status', width: 115 },
  ], []);

  /** One row per asbestos type — the panel under an expanded slide. */
  const typeColumns: AppColumn<PlmReferenceTypeRow>[] = useMemo(() => [
    { field: 'asbestosType', header: 'Asbestos type', width: 150, sortable: false, cellClass: () => 'plm-reflib-strong' },
    { field: 'quantity', header: '%', width: 70, align: 'right', sortable: false, text: (t) => `${t.quantity}%` },
    {
      field: 'rangeMode', header: 'Acceptable range', width: 190, sortable: false,
      text: (t) => `${rangeText(effectiveRange(t))} (${t.rangeMode.toLowerCase()})`,
      render: (t) => {
        const rule = ruleRange(t.quantity);
        return (
          <span>
            {rangeText(effectiveRange(t))}{' '}
            {t.rangeMode === 'Override'
              ? (
                <span className="plm-reflib-tag plm-reflib-tag--ov"
                  title={`Override${t.overrideSource ? ` — ${t.overrideSource}` : ''}. The rule would give ${rangeText(rule)}`}>
                  override
                </span>
              )
              : <span className="plm-reflib-tag" title={RANGE_RULE_HINT}>rule</span>}
          </span>
        );
      },
    },
    { field: 'birefringence', header: 'Birefringence', width: 170, sortable: false, text: (t) => birefringenceLabel(t.birefringence) },
    { field: 'pleochroism', header: 'Pleochroism', width: 105, sortable: false },
    { field: 'morphology', header: 'Morphology', width: 110, sortable: false },
    { field: 'extinction', header: 'Extinction', width: 105, sortable: false },
    { field: 'signOfElongation', header: 'Sign', width: 90, sortable: false },
    { field: 'meanRiAlpha', header: 'Mean RI', width: 150, sortable: false, text: meanRiText },
    { field: 'riAlphaLow', header: 'RI limits', width: 230, sortable: false, text: riLimitsText },
  ], []);

  const typesPanel = (r: PlmReferenceSlideRow) => (
    <div className="plm-reflib-child">
      <AppDataGrid<PlmReferenceTypeRow>
        rows={r.types}
        columns={typeColumns}
        getRowId={(t) => t.key}
        showFilters={false}
        hideFooter
        hideHeaderTools
        autoFocusFirstRow={false}
        emptyText="No asbestos — ND reference (blank slide / material)."
      />
      <div className="plm-reflib-childbar">
        {perm.write && (
          <button type="button" className="ca-btn ca-btn--sm" onClick={() => openDetail(r.id, true)}>
            <span className="tb-i tb-i-green">＋</span> Add asbestos type
          </button>
        )}
        <span className="plm-reflib-hint">
          {r.types.length > 1
            ? `${r.types.length} types — each has its own range and optics. Daily QC reads single-type and ND slides.`
            : (perm.write ? 'Opens the record with a new type row.' : '')}
        </span>
      </div>
    </div>
  );

  // ── detail view (replaces the list, standing rule 12) ──────────────────────

  const typeNames = useMemo(() => lookups.asbestosTypes.map((o) => o.name), [lookups.asbestosTypes]);

  const slideConfig: DetailConfig = useMemo(() => ({
    idField: 'oid',
    basePath: `${PLM_REF_LIB_BASE}/samples`,
    formName: PLM_REF_LIB_FORM_NAME,
    entityLabel: 'Reference Slide',
    columnMajor: true,
    sideLabels: true,
    // Square, un-coloured General Information controls (owner request 2026-10-12: remove
    // the editable-yellow background + rectangular/square corners, THIS form only). Shared
    // .sd-grid--square variant — editable boxes plain white at rest, all control boxes
    // border-radius 0; read-only grey + required-empty yellow cues preserved.
    squareControls: true,
    // The prototype opens a record ready to edit; "+ Add asbestos type" from the
    // list lands on a new type row, which a read-only first view would hide.
    detailOpenInEdit: true,
    fields: [
      { key: 'referenceId', label: 'Reference ID', type: 'readonly', previewUrl: `${PLM_REF_LIB_BASE}/samples/next-id` },
      // Pick an existing Source / Storage OR type a new one (EAS ricSource / ricStorage).
      { key: 'source', label: 'Source', type: 'select', required: true, allowCustom: true, options: lookups.sources },
      { key: 'originalSourceId', label: 'Original Source ID', type: 'text', required: true, maxLength: 100 },
      { key: 'matrixOid', label: 'Matrix', type: 'select', required: true, options: lookups.matrices },
      { key: 'materialOid', label: 'Material', type: 'select', required: true, options: lookups.materials },
      { key: 'storage', label: 'Storage', type: 'select', allowCustom: true, options: lookups.storages },
      { key: 'texture', label: 'Texture', type: 'text', maxLength: 100 },
      { key: 'colorOid', label: 'Color', type: 'select', options: lookups.colors },
      { key: 'homogeneity', label: 'Homogeneity', type: 'select', options: YES_NO.map((v) => ({ id: v, name: v })) },
      { key: 'status', label: 'Status', type: 'readonly' },
      {
        key: 'attachmentOid', label: 'Certificate', type: 'file', fileNameKey: 'attachmentName',
        uploadUrl: `${PLM_REF_LIB_BASE}/file/upload`, downloadUrlBase: `${PLM_REF_LIB_BASE}/file`,
      },
    ],
    recordActions: [
      {
        key: 'retire', label: 'Retire', icon: '⏻', path: 'retire',
        confirmTitle: 'Retire reference slide',
        confirmMessage: `Retire this reference slide? ${RETIRE_MESSAGE}`,
        successMessage: 'Retire completed successfully.',
        // Retire is this form's Delete — hidden without the Delete right.
        visibleWhen: (v) => perm.delete && v.status !== 'Retired',
        reasonPrompt: { placeholder: 'Reason' },
      },
      {
        key: 'reactivate', label: 'Re-activate', icon: '↺', path: 'reactivate',
        confirmTitle: 'Re-activate',
        confirmMessage: 'Make this slide available for reference reads again?',
        successMessage: 'Re-activate completed successfully.',
        visibleWhen: (v) => perm.write && v.status === 'Retired',
      },
    ],
  }), [lookups, perm.write, perm.delete]);

  if (detail) {
    const backToList = () => {
      setDetail(null);
      void loadSlides();
    };
    return (
      <SettingsDetailPage
        config={slideConfig}
        id={detail.id}
        title="Reference Library"
        group="Settings"
        // Previous / next walk the list as it is filtered, like the prototype.
        siblingIds={visible.map((r) => r.id)}
        onHome={backToList}
        onNavigate={(id) => setDetail({ id })}
        onClose={backToList}
        onSaved={() => {
          // A Source / Storage typed on the detail becomes an option too.
          void loadLookups();
          void loadSlides();
        }}
        bufferedChild={(ctx) => (
          <PlmReferenceTypesEditor {...ctx} typeOptions={typeNames} addTypeOnOpen={detail.addType} />
        )}
      />
    );
  }

  const editOne = () => {
    if (selected.length === 1) openDetail(selected[0]!.id);
    else notify.warning('Select one reference slide first (tick its row), or double-click it.');
  };

  return (
    <div className="bl-page mlv-host plm-reflib-page">
      <div className="bl-grid-area">
        {/* 5px padding top & bottom on the breadcrumb (owner request 2026-10-14). */}
        <div className="dp-breadcrumb" style={{ padding: '5px 0' }}>
          <a onClick={onHome} style={{ cursor: 'pointer' }}>Home</a>
          <i className="ti ti-chevron-right" />
          <span>Settings</span>
          <i className="ti ti-chevron-right" />
          {/* Analysis subgroup node (owner request 2026-10-14 — subgroup on all Settings forms). */}
          <span>Analysis</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Reference Library</span>
        </div>

        {/* Title, icon, colour and the ONE load-time chip come from the nav entry
            via navMeta — they cannot drift from the sidebar (rule 7). */}
        <FormTitle
          pageId={PLM_REF_LIB_PAGE_ID}
          loadMs={loadMs}
          loading={loading}
          auditFormName={PLM_REF_LIB_FORM_NAME}
        />

        {/* Canonical order: [PLM | PCM] · New · Edit · Delete · Refresh · Export to. */}
        <AppToolbar
          left={(
            <span className="plm-seg" role="group" aria-label="Test" data-unsaved-ignore>
              {(['PLM', 'PCM'] as const).map((t) => (
                <button key={t} type="button" className={test === t ? 'on' : ''} aria-pressed={test === t}
                  onClick={() => { setTest(t); setSel(new Set()); }}>
                  {t}
                </button>
              ))}
            </span>
          )}
          onNew={perm.create ? () => openDetail(null) : undefined}
          newDisabled={!plm}
          onEdit={perm.write ? editOne : undefined}
          editDisabled={!plm || loading || selected.length !== 1}
          onDelete={perm.delete ? () => { void retireSelected(); } : undefined}
          deleteDisabled={!plm || selected.length === 0}
          onRefresh={() => { void loadSlides(); }}
          refreshing={loading}
          exportData={{
            filename: 'Reference Library',
            ...gridExportMatrix(visible, plm ? columns : pcmColumns),
          }}
        />

        <GridSearch
          value={search}
          onChange={setSearch}
          placeholder="Search ID, source, type…"
          right={(
            <span className="plm-reflib-chips" role="group" aria-label="Status" data-unsaved-ignore>
              {STATUS_FILTERS.map((s) => (
                <button key={s} type="button" className={`plm-reflib-chip${status === s ? ' on' : ''}`}
                  aria-pressed={status === s} onClick={() => setStatus(s)}>
                  {s} <b>{counts[s]}</b>
                </button>
              ))}
            </span>
          )}
        />

        {/* The grid stays a DIRECT child of .bl-grid-area — the page's flex layout
            (`.bl-grid-area > .adg`) is what gives it the height that is left. */}
        <AppDataGrid<PlmReferenceSlideRow>
          rows={visible}
          columns={plm ? columns : pcmColumns}
          getRowId={(r) => r.id}
          loading={loading}
          selectable={plm}
          selectedIds={sel}
          onSelectionChange={setSel}
          onRowDoubleClick={plm && perm.write ? (r) => openDetail(r.id) : undefined}
          rowDetail={plm
            ? { render: typesPanel, expandedIds: open, onExpandedChange: setOpen, expandTitle: 'Show asbestos types' }
            : undefined}
          defaultPageSize={50}
          exportFilename="Reference Library"
          footerNote={perm.write ? 'Double-click a row to open it.' : undefined}
          emptyText={plm
            ? 'No reference slides match — use New to add one'
            : 'No PCM reference slides yet — the PCM fields are waiting on the lab’s PCM slide details.'}
        />

        <ControlLimitsPanel lookups={lookups} plm={plm} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface LimitGridRow extends CalculatedLimit {
  id: string;
  concentration: string;
  active: string;
  remark: string;
}

/**
 * Control Limits — per concentration category (prototype v7; BTLIMS "Control
 * Limits with Analyst"). Collapsed until opened; the QC reads are fetched on the
 * first open, never with the list.
 *
 * Calculate works the limits out from the lab's QC reads of the last
 * {@link LIMIT_WINDOW_MONTHS} months that have any — the SAME points, pairing
 * and error rule as the PLM QC charts (plmQcStats), so a limit here and a chart
 * there cannot disagree. Upload as active stores the reviewed set (with the
 * remark of each row) as the active limits of the scope and analyst; the set it
 * replaces is kept as history and every changed value is audited.
 */
function ControlLimitsPanel({ lookups, plm }: { lookups: PlmReferenceLookups; plm: boolean }) {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [scope, setScope] = useState<LimitScope>(1);
  const [analystId, setAnalystId] = useState('');
  const [basis, setBasis] = useState<LimitBasis>('sd');
  const [remarks, setRemarks] = useState<Record<number, string>>({});

  const [opened, setOpened] = useState(false);
  const [qc, setQc] = useState<{ std: QcPoint[]; pairs: QcPoint[] } | null>(null);
  const [qcLoading, setQcLoading] = useState(false);
  const [active, setActive] = useState<PlmActiveLimit[]>([]);
  const [uploading, setUploading] = useState(false);

  // Upload as active replaces the stored limit set — Write only (hidden otherwise).
  const canUpload = useNavActions(PLM_REF_LIB_PAGE_ID).write;
  const analystName = lookups.analysts.find((a) => a.id === analystId)?.name ?? '';
  const ready = plm && canLoadLimits(scope, analystId);

  /** The QC reads the limits are calculated from — the same two feeds the PLM QC
   *  charts read. `announce` = the user pressed Calculate. */
  const loadQc = useCallback(async (announce: boolean) => {
    setQcLoading(true);
    try {
      const [history, daily] = await Promise.all([fetchPlmQcHistory(), fetchPlmDailyQcList()]);
      setQc({ std: dailyQcPoints(daily), pairs: toPoints(history) });
      if (announce) notify.success('Calculate completed successfully.');
    } catch {
      notify.error('Could not read the QC history. Please try again.');
    } finally {
      setQcLoading(false);
    }
  }, [notify]);

  const loadActive = useCallback(async (s: LimitScope, a: string) => {
    if (!canLoadLimits(s, a)) { setActive([]); return; }
    const res = await api.get<{ data?: Record<string, unknown>[] }>(
      `${PLM_REF_LIB_BASE}/limits`,
      { params: scopeNeedsAnalyst(s) ? { scope: s, analystId: a } : { scope: s } });
    setActive((res.data?.data ?? []).map(toActiveLimit));
  }, []);

  useEffect(() => { if (opened && plm && !qc) void loadQc(false); }, [opened, plm, qc, loadQc]);
  useEffect(() => { if (opened && plm) void loadActive(scope, analystId); }, [opened, plm, scope, analystId, loadActive]);

  // The slice's points and their limits — recalculated whenever a choice changes.
  const points = useMemo(
    () => (qc && ready ? recentMonths(limitSeries(scope, analystName, qc.std, qc.pairs)) : []),
    [qc, ready, scope, analystName]);
  const calc = useMemo(() => (ready && qc ? calculateLimits(points, basis) : []), [ready, qc, points, basis]);

  const rows: LimitGridRow[] = useMemo(() => calc.map((r) => {
    const a = active.find((x) => x.category === String(r.category));
    return {
      ...r,
      id: String(r.category),
      concentration: categoryLabel(r.category),
      active: a && a.lcl && a.ucl ? `${a.lcl} … ${a.ucl}` : '',
      remark: remarks[r.category] ?? '',
    };
  }), [calc, active, remarks]);

  const columns: AppColumn<LimitGridRow>[] = useMemo(() => [
    { field: 'id', header: 'Category', width: 95, sortable: false, cellClass: () => 'plm-reflib-strong' },
    { field: 'concentration', header: 'Concentration', width: 130, sortable: false },
    {
      field: 'n', header: 'No. of counts', width: 130, align: 'right', sortable: false,
      text: (r) => String(r.n),
      render: (r) => (
        <span>
          {r.n > 0 && r.n < FEW_COUNTS && (
            <span className="plm-reflib-tag plm-reflib-tag--ov" title="Few counts — review before upload">few</span>
          )}{' '}
          {r.n}
        </span>
      ),
    },
    { field: 'mean', header: 'Mean', width: 90, align: 'right', sortable: false, text: (r) => (r.n ? fmt2(r.mean) : '') },
    { field: 'sd', header: 'SD', width: 90, align: 'right', sortable: false, text: (r) => (r.n ? fmt2(r.sd) : '') },
    { field: 'iqr', header: 'IQR', width: 90, align: 'right', sortable: false, text: (r) => (r.n ? fmt2(r.iqr) : '') },
    { field: 'lcl', header: 'LCL', width: 90, align: 'right', sortable: false, text: (r) => (r.n ? fmt2(r.lcl) : '') },
    { field: 'ucl', header: 'UCL', width: 90, align: 'right', sortable: false, text: (r) => (r.n ? fmt2(r.ucl) : '') },
    {
      field: 'active', header: 'Active limits', width: 160, sortable: false,
      render: (r) => (r.active
        ? <span className="adg-pill ok">{r.active}</span>
        : <span className="plm-reflib-muted">none</span>),
    },
    // Remarks only travel with Upload as active, so they are editable only with Write.
    { field: 'remark', header: 'Remark', width: 280, sortable: false, edit: canUpload ? { type: 'text' } : undefined },
  ], [canUpload]);

  const upload = async () => {
    if (!ready || !hasAnyLimit(calc)) return;
    const who = scopeNeedsAnalyst(scope) ? ` for ${analystName}` : '';
    const ok = await confirm({
      title: 'Upload control limits',
      message: `Make these ${basis.toUpperCase()} limits the active ${scopeLabel(scope)} limits${who}? `
        + 'The previous set is kept in history (audited).',
      confirmLabel: 'Upload',
    });
    if (!ok) return;
    setUploading(true);
    try {
      await runLoading(api.post(`${PLM_REF_LIB_BASE}/limits/upload`, {
        scope,
        analystOid: scopeNeedsAnalyst(scope) ? analystId : null,
        basis: basis.toUpperCase(),
        rows: toLimitUploadRows(calc, remarks),
      }), { blocking: true, label: 'Uploading…' });
      notify.success('Upload completed successfully.');
      setRemarks({});
      await loadActive(scope, analystId);
    } catch (e) {
      notify.error(serverMessage(e) ?? 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const uploadedOn = active.find((a) => a.createdAt)?.createdAt ?? '';
  const uploadedBasis = active.find((a) => a.basis)?.basis ?? '';
  const note = !plm
    ? ''
    : !ready
      ? 'Select an analyst to calculate their control limits.'
      : `${scopeLabel(scope)}${scopeNeedsAnalyst(scope) ? ` · ${analystName}` : ' (all analysts pooled)'}`
        + ` · value: ${scope === 1 ? 'read − reference' : 'r1 − r2'}`
        + ` · ${points.length} QC read${points.length === 1 ? '' : 's'} from the last ${LIMIT_WINDOW_MONTHS} months with data. `
        + (active.some((a) => a.lcl && a.ucl)
          ? `Active limits uploaded ${fmtLocalDate(uploadedOn)}${uploadedBasis ? ` (${uploadedBasis})` : ''}.`
          : 'No limits uploaded for this selection.');

  return (
    <details className="plm-reflib-limits" onToggle={(e) => { if ((e.target as HTMLDetailsElement).open) setOpened(true); }}>
      <summary className="plm-reflib-limits-title">
        Control Limits <span className="plm-reflib-hint">— per concentration category (click to open)</span>
      </summary>

      <div className="plm-reflib-limits-head" data-unsaved-ignore>
        <span className="plm-seg" role="group" aria-label="Control limit scope">
          {LIMIT_SCOPES.map((s) => (
            <button key={s.value} type="button" className={scope === s.value ? 'on' : ''}
              aria-pressed={scope === s.value} disabled={!plm}
              onClick={() => { setScope(s.value); setRemarks({}); }}>
              {s.label}
            </button>
          ))}
        </span>

        <label className="plm-reflib-analyst">
          <span>Analyst</span>
          <ComboSelect
            value={scopeNeedsAnalyst(scope) ? analystId : ''}
            options={lookups.analysts.map((a) => ({ value: a.id, label: a.name }))}
            onChange={(v) => { setAnalystId(v); setRemarks({}); }}
            placeholder={scopeNeedsAnalyst(scope) ? 'Select analyst…' : 'All analysts'}
            // Within Laboratory pools every analyst, so there is no one to pick.
            disabled={!plm || !scopeNeedsAnalyst(scope)}
          />
        </label>

        <label className="plm-reflib-basis">
          <span>Basis</span>
          <select className="sd-input" value={basis} disabled={!plm}
            onChange={(e) => setBasis(e.target.value as LimitBasis)}>
            {LIMIT_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
        </label>

        <span className="plm-reflib-sp" />

        <button type="button" className="ca-btn" disabled={!ready || qcLoading}
          title="Re-read the QC history and recalculate" onClick={() => { void loadQc(true); }}>
          <BusyIcon busy={qcLoading} icon="ti-calculator" /> Calculate
        </button>
        {canUpload && (
          <button type="button" className="ca-btn ca-btn-solid" disabled={!ready || uploading || qcLoading || !hasAnyLimit(calc)}
            onClick={() => { void upload(); }}>
            <BusyIcon busy={uploading} icon="ti-upload" /> Upload as active
          </button>
        )}
      </div>

      <AppDataGrid<LimitGridRow>
        rows={rows}
        columns={columns}
        getRowId={(r) => r.id}
        loading={qcLoading && !qc}
        showFilters={false}
        hideFooter
        autoFocusFirstRow={false}
        exportFilename="Reference Library Control Limits"
        onCellCommit={(row, col, v) => { if (col.field === 'remark') setRemarks((m) => ({ ...m, [row.category]: v })); }}
        onCellChange={(row, col, v) => { if (col.field === 'remark') setRemarks((m) => ({ ...m, [row.category]: v })); }}
        emptyText={!plm
          ? 'PCM control limits — to be defined with the lab (PCM uses relative precision, Sr, rather than % categories).'
          : 'Select an analyst to calculate their control limits'}
      />
      <div className="plm-reflib-hint plm-reflib-limits-note">{note}</div>
    </details>
  );
}

export default PlmReferenceLibraryPage;
